/**
 * DashboardShell — shared layout wrapper for all dashboard plugins.
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { Box, Alert, CircularProgress, Fade, Typography } from "@mui/material";
import DashboardHeader from "./DashboardHeader";
import AISummaryCard from "./AISummaryCard";
import ComparisonPanel from "./plugins/spbAnalytics/ComparisonPanel";
import { useComparisonData } from "./plugins/spbAnalytics/useSPBAnalyticsData";
import { computeBaselineDates } from "./utils/dateUtils";
import { buildShareUrl } from "./utils/shareUtils";
import type {
  DashboardPlugin,
  PluginDataResult,
  SecondTableFilters,
} from "./types";

interface DashboardShellProps {
  plugin: DashboardPlugin;
  effectiveRole?: string;
  currentTeamId?: number;
  userId?: number;
  userAttributes?: any;
  darkMode?: boolean;
  onToggleDark?: () => void;
}

const DEFAULT_CMP_FILTERS: SecondTableFilters = {
  geo: "ww",
  device: "Desktop-Mobile",
  targetStartDate: "",
  targetEndDate: "",
  baselineStartDate: "",
  baselineEndDate: "",
  alignment: "W",
  comparisonMode: "YoY",
};

const DashboardShell: React.FC<DashboardShellProps> = ({
  plugin,
  effectiveRole,
  currentTeamId,
  userId,
  userAttributes,
  darkMode,
  onToggleDark,
}) => {
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const hasAutoApplied = useRef(false);
  const pendingComparisonAfterLoad = useRef(false);

  // Comparison state
  const [cmpFilters, setCmpFilters] =
    useState<SecondTableFilters>(DEFAULT_CMP_FILTERS);
  const comparison = useComparisonData();

  const { data, isLoading, error, refetch }: PluginDataResult =
    plugin.useData(appliedFilters);

  // Auto-apply on first load when dates are available
  useEffect(() => {
    if (
      !hasAutoApplied.current &&
      filters.targetStartDate &&
      filters.baselineStartDate
    ) {
      hasAutoApplied.current = true;
      setAppliedFilters({ ...filters });
      if (filters.secondTableEnabled) {
        pendingComparisonAfterLoad.current = true;
      }
    }
  }, [filters]);

  // Sync comparison filters when primary filters change (copy dates/geo/device)
  useEffect(() => {
    if (filters.targetStartDate && !cmpFilters.targetStartDate) {
      const baseline = computeBaselineDates(
        filters.targetStartDate,
        filters.targetEndDate,
        cmpFilters.comparisonMode
      );
      setCmpFilters((prev: SecondTableFilters) => ({
        ...prev,
        geo: filters.geo ?? prev.geo,
        device: filters.device ?? prev.device,
        targetStartDate: filters.targetStartDate,
        targetEndDate: filters.targetEndDate,
        baselineStartDate: baseline.start,
        baselineEndDate: baseline.end,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.targetStartDate, filters.targetEndDate]);

  // Clear comparison data when toggle is turned off
  const secondTableEnabled = filters.secondTableEnabled ?? false;
  useEffect(() => {
    if (!secondTableEnabled) {
      comparison.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondTableEnabled]);

  const handleLoadComparison = useCallback(
    (primaryData?: any) => {
      const d = primaryData ?? data;
      let primaryScale = (d as any)?.reportData?.scale;
      let primaryBridgeScale = (d as any)?.reportData?.bridgeScale;
      if (primaryScale == null && d) {
        const rd = (d as any)?.reportData;
        if (rd?.metricsTable?.rows?.length > 0) {
          const revRow = rd.metricsTable.rows.find((r: any) =>
            r.label?.startsWith("Revenue USD")
          );
          if (revRow?.cells?.[0]?.rawValue != null) {
            const abs = Math.abs(revRow.cells[0].rawValue);
            if (abs >= 1e9) primaryScale = 1e9;
            else if (abs >= 1e6) primaryScale = 1e6;
            else if (abs >= 1e3) primaryScale = 1e3;
            else primaryScale = 1;
          }
        }
      }
      comparison.fetchComparison(
        { ...cmpFilters, product: filters.product ?? "SP" },
        primaryScale,
        primaryBridgeScale
      );
    },
    [cmpFilters, filters.product, comparison, data]
  );

  // When primary finishes loading and comparison is pending, fire it with fresh data
  const prevIsLoading = useRef(false);
  useEffect(() => {
    if (
      prevIsLoading.current &&
      !isLoading &&
      data &&
      pendingComparisonAfterLoad.current
    ) {
      pendingComparisonAfterLoad.current = false;
      handleLoadComparison(data);
    }
    prevIsLoading.current = isLoading;
  }, [isLoading, data, handleLoadComparison]);

  // Unified apply — loads primary and comparison in one click
  const handleApply = useCallback(() => {
    setAppliedFilters({ ...filters });
    if (filters.secondTableEnabled) {
      if (!isLoading && data) {
        const primaryChanged =
          filters.product !== appliedFilters.product ||
          filters.geo !== appliedFilters.geo ||
          filters.device !== appliedFilters.device ||
          filters.targetStartDate !== appliedFilters.targetStartDate ||
          filters.targetEndDate !== appliedFilters.targetEndDate;

        if (primaryChanged) {
          // Primary filters changed — wait for reload to finish, then fire comparison
          pendingComparisonAfterLoad.current = true;
        } else {
          // Only comparison filters changed — primary data is still valid, fire immediately
          handleLoadComparison(data);
        }
      } else {
        // Primary is loading or no data yet — wait for it
        pendingComparisonAfterLoad.current = true;
      }
    }
  }, [filters, appliedFilters, isLoading, data, handleLoadComparison]);

  // Plugins can override the CSV download for tab-local state (e.g. the
  // Historical Trends sub-view). When the registered exporter is non-null
  // it takes precedence over plugin.exportCsv.
  const tabExporterRef = useRef<
    (() => { csv: string; filename: string }) | null
  >(null);
  const registerCsvExporter = useCallback(
    (fn: (() => { csv: string; filename: string }) | null) => {
      tabExporterRef.current = fn;
    },
    []
  );
  // A self-contained plugin (owns its own filters) can register a Share-URL
  // builder; the header's Share button uses it in preference to the shell's
  // default filter serialization.
  const shareBuilderRef = useRef<(() => string) | null>(null);
  const registerShareBuilder = useCallback((fn: (() => string) | null) => {
    shareBuilderRef.current = fn;
  }, []);

  const handleDownloadCsv = useCallback(() => {
    let csv = "";
    let filename = `${plugin.id}-export.csv`;
    if (tabExporterRef.current) {
      const out = tabExporterRef.current();
      csv = out.csv;
      filename = out.filename;
    } else if (plugin.exportCsv && data) {
      csv = plugin.exportCsv(data, comparison.data);
    } else {
      return;
    }
    if (!csv) return;
    // Prepend a UTF-8 BOM (U+FEFF) so Excel (especially on macOS) decodes
    // em-dashes and other non-ASCII characters correctly instead of showing
    // mojibake like `‚Äî`.
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }, [plugin, data, comparison.data]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Build a shareable URL for the currently applied view (product, geo,
  // device, dates, alignment, comparison, currency). Uses appliedFilters so
  // the link reflects what's actually loaded on screen, not an un-applied
  // edit in the filter bar.
  const handleShare = useCallback(() => {
    // A self-contained plugin's own builder wins (it knows its filter state);
    // otherwise fall back to the shell's applied-filter serialization.
    if (shareBuilderRef.current) return shareBuilderRef.current();
    return buildShareUrl({
      ...appliedFilters,
      currency: filters.currency,
    });
  }, [appliedFilters, filters.currency]);

  const { FilterPanel, ReportContent } = plugin;
  const aiEnabled = plugin.aiSummary?.enabled ?? false;
  const aiData = data as any;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pb: 4 }}>
      <DashboardHeader
        title={plugin.displayName}
        onDownloadCsv={
          plugin.id === "weblab-tracker"
            ? currentTeamId === 104 &&
              (effectiveRole === "admin" || effectiveRole === "operator")
              ? handleDownloadCsv
              : undefined
            : // Plugins that declare no exportCsv (e.g. audit-console) have
            // nothing to download, so the header button is hidden instead
            // of rendering a dead control. Plugins with exportCsv keep the
            // button; a tab-registered exporter still takes precedence on
            // click, exactly as before.
            plugin.exportCsv
            ? handleDownloadCsv
            : undefined
        }
        onPrint={handlePrint}
        onShare={handleShare}
        darkMode={darkMode}
        onToggleDark={onToggleDark}
      />

      <FilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApply}
        isLoading={isLoading}
      />

      {plugin.supportsComparison && secondTableEnabled && (
        <ComparisonPanel
          enabled={secondTableEnabled}
          filters={cmpFilters}
          onFiltersChange={setCmpFilters}
          onLoadComparison={handleLoadComparison}
          isLoading={comparison.isLoading}
        />
      )}

      {/* Shell-level loading banner only on the very FIRST load (no data
          yet). On refetches (e.g. team switch) the plugin content stays
          mounted and shows its own inline loading treatment, avoiding two
          competing spinners and a large layout jump. */}
      {isLoading && !data && (
        <Fade in timeout={300}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              py: 6,
            }}
          >
            <CircularProgress size={28} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              Loading dashboard data…
            </Typography>
          </Box>
        </Fade>
      )}

      {error && !isLoading && (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {aiEnabled && data && !isLoading && !error && (
        <AISummaryCard
          marketplaceAnalysis={aiData?.marketplaceAnalysis ?? null}
          trendAnalysis={aiData?.trendAnalysis ?? null}
          isLoading={aiData?.aiLoading ?? false}
          error={aiData?.aiError ?? null}
        />
      )}

      {
        <ReportContent
          data={data}
          isLoading={isLoading}
          error={error}
          comparisonData={comparison.data}
          comparisonLoading={comparison.isLoading}
          comparisonError={comparison.error}
          registerCsvExporter={registerCsvExporter}
          registerShareBuilder={registerShareBuilder}
          filters={{ ...appliedFilters, currency: filters.currency } as any}
          refetch={refetch}
          effectiveRole={effectiveRole}
          currentTeamId={currentTeamId}
          userId={userId}
          onFiltersChange={setAppliedFilters}
        />
      }
    </Box>
  );
};

export default DashboardShell;
